
    // IMPORTS
    import { computed, defineComponent, reactive, ref } from 'vue';
    import router from '@/router';

    import { Input } from '@progress/kendo-vue-inputs'
    import { Button } from '@progress/kendo-vue-buttons'
    import { Error } from '@progress/kendo-vue-labels'
    import loginContent from './login-content.vue'

    import { AuthenticationApi, buildLocalization, RouteUtil, StringUtil } from '@/util'
    import { AxiosError, AxiosResponse, HttpStatusCode } from 'axios';
    import { ILinkDTO } from '@/types/ILinkDTO'
    import { IPasswordResetResultDTO } from '@/types/auth';

    export default defineComponent({
        name: 'PasswordResetPage',

        components: {
            loginContent,
            'k-input': Input,
            'k-button': Button,
            Error
        },

        setup() {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const username = RouteUtil.getRouteParam('user');            
            const token = RouteUtil.getRouteParam('token');

            // Token and username are required
            if (username == null || token == null) {
                router.push("/");
            }

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const data = reactive({
                username: username,
                newPassword: null as (string | null),
                confirmPassword: null as (string | null),
            });

            const state = reactive({
                resetAttempt: false,
                errors: [] as string[],
                resetInProgress: false,
                completed: false
            })

            const refNewPassword = ref<InstanceType<typeof Input> | null>(null);
            const refConfirmPassword = ref<InstanceType<typeof Input> | null>(null);

            const publicData = { data, state, refNewPassword, refConfirmPassword }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const title = computed(() => localize('PasswordReset'))
            const links = computed<ILinkDTO[]>(() => [
                {
                    name: state.completed
                        ? localize('LogIn')
                        : localize('Back'),
                    url: '/auth'
                },
            ])

            const newPasswordHasContent = computed(() => StringUtil.hasContent(data.newPassword))
            const confirmPasswordHasContent = computed(() => StringUtil.hasContent(data.confirmPassword))
            const entriesDeviate = computed(() => newPasswordHasContent.value &&             
                                                    confirmPasswordHasContent.value &&             
                                                    (data.newPassword != data.confirmPassword))

            const validEntries = computed(() => newPasswordHasContent.value &&             
                                                    confirmPasswordHasContent.value &&             
                                                    (data.newPassword === data.confirmPassword) )


            const anyErrors = computed(() => Boolean(state.errors?.length))

            const computedData = {
                title,
                links,
                newPasswordHasContent,
                confirmPasswordHasContent,
                entriesDeviate,
                validEntries,
                anyErrors
            }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const { localize } = buildLocalization();                      
            
            function onEnter(fieldName: string) {
                switch(fieldName) {
                    case 'newPassword':
                        if (newPasswordHasContent.value) {
                            if (refConfirmPassword.value?.focus) {
                                refConfirmPassword.value.focus();
                            }
                        }
                        else {
                            state.resetAttempt = true;
                        }
                        break;

                    case 'confirmPassword': 
                        if (confirmPasswordHasContent.value) {
                            resetPassword();
                        }
                        else {
                            state.resetAttempt = true;
                        }
                        break;
                }
            }

            function resetPassword() {
                state.resetAttempt = true;

                if (validEntries.value) {
                    performPasswordReset(username as string, token as string, data.newPassword?.trim() as string);
                }
            }

            const publicMethods = { localize, onEnter, resetPassword }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function performPasswordReset(username: string, token: string, newPassword: string) {
                const api = new AuthenticationApi();

                state.resetInProgress = true;

                api.resetPassword({ username, password: newPassword, token })
                .then(response => handleResponse(response))
                .catch((err: AxiosError<IPasswordResetResultDTO>) => handleResponse(err.response))
                .finally(() => state.resetInProgress = false)  
            }

            function handleResponse(response: AxiosResponse<IPasswordResetResultDTO> | undefined) {
                if (response?.status === HttpStatusCode.Ok) {
                    const result = response.data;

                    if (result.success) {
                        state.completed = true;
                        return;
                    }

                    if (result.errorCodes?.length) {
                        state.errors = result.errorCodes;
                        return;
                    }
                }

                state.errors = [ 'UnexpectedError' ];
            }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
